import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/auth/useAuth';
import { fetchInviteByToken } from '@/services/organizations';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { LOG_PREFIX } from '@/config/constants';
import type { InvitePreview } from '@/types';

/**
 * Public landing page for an organization invite link (/accept-invite?token=…).
 * Validates the token, then lets the invitee set a password and join the org.
 * Accepting provisions their manager profile and logs them in.
 */
export function AcceptInvitePage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const auth = useAuth();
  const navigate = useNavigate();

  const [invite, setInvite] = useState<InvitePreview | null>(null);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Already signed in (e.g. accepted, then refreshed) → go to the dashboard.
  useEffect(() => {
    if (auth.isAuthenticated && !auth.isLoading) {
      navigate('/', { replace: true });
    }
  }, [auth.isAuthenticated, auth.isLoading, navigate]);

  useEffect(() => {
    if (token === '') {
      setLookupError('This invite link is missing its token.');
      setLoading(false);
      return;
    }
    let cancelled = false;
    const load = async () => {
      try {
        const preview = await fetchInviteByToken(token);
        if (cancelled) return;
        if (!preview) {
          setLookupError('This invite link is invalid.');
        } else if (preview.accepted_at) {
          setLookupError('This invite has already been used.');
        } else if (new Date(preview.expires_at).getTime() <= Date.now()) {
          setLookupError('This invite link has expired. Ask an admin for a new one.');
        } else {
          setInvite(preview);
        }
      } catch (err) {
        console.error(LOG_PREFIX, 'Failed to look up invite:', err);
        if (!cancelled) {
          setLookupError(
            typeof err === 'object' && err && 'message' in err
              ? String((err as { message: unknown }).message)
              : 'Could not load this invite.',
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!invite) return;
    setSubmitting(true);
    try {
      await auth.acceptInvite({
        email: invite.email,
        password,
        displayName: displayName.trim() || invite.email,
        token,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-brand-purple to-brand-purple-light">
      <div className="w-full max-w-md rounded-xl bg-white p-10 shadow-2xl">
        <div className="mb-8 text-center">
          <h1 className="mb-2 text-3xl font-bold text-brand-purple">PathChron</h1>
          <p className="text-sm text-gray-500">Enterprise Device Management</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner />
          </div>
        ) : lookupError ? (
          <div className="text-center">
            <h2 className="text-lg font-semibold text-brand-black">Invite unavailable</h2>
            <p className="mt-2 text-sm text-gray-500">{lookupError}</p>
            <Link
              to="/login"
              className="mt-6 inline-block text-sm font-medium text-brand-purple hover:underline"
            >
              Go to sign in
            </Link>
          </div>
        ) : (
          invite && (
            <>
              <div className="mb-6 text-center">
                <h2 className="text-lg font-semibold text-brand-black">
                  You&apos;ve been invited
                </h2>
                <p className="mt-1 text-sm text-gray-400">
                  Join <span className="font-medium text-brand-black">{invite.organization_name}</span>{' '}
                  as <span className="font-medium text-brand-black">{invite.role}</span>
                </p>
              </div>

              {auth.error && (
                <div className="mb-4 rounded-lg bg-brand-orange-light p-3 text-sm text-brand-orange">
                  {auth.error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="invite-email" className="mb-1 block text-sm font-medium text-brand-black">
                    Email
                  </label>
                  <input
                    id="invite-email"
                    type="email"
                    readOnly
                    value={invite.email}
                    className="w-full cursor-not-allowed rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-500 outline-none"
                  />
                </div>
                <div>
                  <label htmlFor="invite-name" className="mb-1 block text-sm font-medium text-brand-black">
                    Your name
                  </label>
                  <input
                    id="invite-name"
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-brand-purple focus:ring-1 focus:ring-brand-purple"
                    placeholder="Jane Doe"
                  />
                </div>
                <div>
                  <label htmlFor="invite-password" className="mb-1 block text-sm font-medium text-brand-black">
                    Password
                  </label>
                  <input
                    id="invite-password"
                    type="password"
                    required
                    minLength={6}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-brand-purple focus:ring-1 focus:ring-brand-purple"
                    placeholder="At least 6 characters"
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex w-full items-center justify-center gap-3 rounded-lg bg-brand-purple px-6 py-3 font-medium text-white shadow-sm transition-colors hover:bg-brand-purple/90 disabled:opacity-50"
                >
                  {submitting ? 'Joining…' : 'Accept invite & join'}
                </button>
              </form>

              <p className="mt-4 text-center text-sm text-gray-500">
                Already have an account?{' '}
                <Link to="/login" className="font-medium text-brand-purple hover:underline">
                  Sign in
                </Link>
              </p>
            </>
          )
        )}
      </div>
    </div>
  );
}
